import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { Button } from '../../components/Button';
import { Flex } from '../../components/Flex';
import { Input } from '../../components/Input';
import { MultiLangLink } from '../../components/MultiLangLink';
import { Text } from '../../components/Text';
import { GoogleButton } from '../../components/logins/GoogleButton';
import { authTestIDs } from '../../constants/test/auth.testIds';
import { AppLangs } from '../../i18n';
import { LoginDto } from '../../model/user/dto/LoginDto';
import { LoginWithGoogleReturnPath } from '../../model/user/types/LoginWithGoogleReturnPath.enum';
import { AtiraLinkActions } from '../../redux/atira-link/atira-link.slice';
import { useAppDispatch } from '../../redux/store';
import { userActions } from '../../redux/user/user.slice';
import { Breakpoints } from '../../theme/Breakpoints';
import { FontSize } from '../../theme/FontSize';
import { Rounded } from '../../theme/Rounded';
import { Spacing } from '../../theme/Spacing';
import { AtiraToast } from '../../utils/AtiraToast';
import { onNavigateWithRedirect } from '../../utils/OnNavigateWithRedirect';
import { Regexes } from '../../utils/String';
import { onSetTypedCRUDErrors } from '../../utils/onSetTypedCRUDErrors';
import { RegisterWrapper } from './RegisterWrapper';

const useMultiLangNavigate = (
  currentLang: string,
  navigate: any,
  keepParams?: boolean,
) => {
  return (path: string, options?: { replace: boolean }) => {
    let newPath = `${currentLang === 'en' ? '' : `/${currentLang}`}${path}`;

    if (keepParams) {
      const search = window.location.search;
      newPath += search;
    }
    navigate(newPath, options);
  };
};

const InnerWrapper = styled(Flex)`
  width: 100%;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: ${Spacing.l};
  padding-top: ${Spacing.m};

  @media (min-width: ${Breakpoints.TABLET}) {
    padding: 0 ${Spacing.m};
  }
`;

const SignUpLink = styled(MultiLangLink)`
  text-decoration: none;
  color: ${(props) => props.theme.main};
  font-size: ${FontSize.m};
`;

const StyledButton = styled(Button)`
  height: 3rem !important;
  width: 100%;
  background-color: ${({ theme }) => theme.main};
  transition: background-color 0.2s;
  margin: 0;
  border-radius: ${Rounded.lg};

  &:hover {
    background-color: ${(props) => props.theme.lightMain};
  }
`;

const PasswordButton = styled(Button)`
  background-color: ${(props) => props.theme.transparent};
  color: #4285f4;
  font-size: ${FontSize.m};
  margin: 0;
  padding: 0;
  margin-inline-start: auto;
`;

const StyledInput = styled(Input)`
  height: 2.5rem;
  border: 1px solid ${(props) => props.theme.lightergray};
  padding: ${Spacing.s};
  transition: border 0.2s;
  font-size: ${FontSize.m};
  border-radius: ${Rounded.lg};

  &:focus {
    border: ${({ theme }) => `1px solid ${theme.main} !important`};
  }
`;

const StyledHr = styled.hr`
  background-color: ${(props) => props.theme.darkerSub};
  height: 1px;
  border: 0;
  opacity: 1;
  width: 100%;
  margin: ${Spacing.m} 0;
`;

const WelcomeMessage = styled(Text)`
  color: ${({ theme }) => theme.main};
  font-weight: 600;
  font-size: ${FontSize.l};
  text-align: center;
  margin-bottom: ${Spacing.m};
  border-bottom: ${({ theme }) => `1px solid ${theme.lightergray}`};
  padding: ${Spacing.s} 0;
  width: 100%;
  white-space: nowrap;
`;

const OuterWrapper = styled(Flex)`
  flex-direction: column;
  padding: 0 ${Spacing.s};

  @media (min-width: ${Breakpoints.TABLET}) {
    padding: 0 ${Spacing.xxl};
  }

  @media (min-width: ${Breakpoints.DESKTOP}) {
    padding: 0 ${Spacing.xxxl};
  }
`;

const PrivacyTermsLink = styled.a`
  font-weight: bold;
  text-decoration: underline;
  color: ${({ theme }) => theme.main};
  font-size: ${FontSize.s};
  transition: all 0.3s;
  text-underline-offset: 5px;

  &:hover {
    color: ${({ theme }) => theme.lightMain};
    text-decoration: underline;
  }
`;

export const Login: React.FC = () => {
  const [loading, setLoading] = useState(false);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const multiLangNavigate = useMultiLangNavigate(
    i18n.language as AppLangs,
    navigate,
    true,
  );

  const { control, handleSubmit, getValues, reset, setError } =
    useForm<LoginDto>({
      defaultValues: { email: '', password: '' },
    });

  const { t } = useTranslation();

  const onNext = async () => {
    try {
      setLoading(true);
      const { user } = await dispatch(userActions.login(getValues())).unwrap();
      await dispatch(
        AtiraLinkActions.getUserAtiraLinks({ userId: user?._id }),
      ).unwrap();

      reset({ email: '', password: '' });
      onNavigateWithRedirect(navigate);
    } catch (e: any) {
      AtiraToast.apiError(e);
      console.log(e);
      onSetTypedCRUDErrors(e, setError);
    } finally {
      setLoading(false);
    }
  };

  return (
    <RegisterWrapper>
      <OuterWrapper>
        <WelcomeMessage>{t('register.welcome_message')}</WelcomeMessage>

        <InnerWrapper>
          <Controller
            name="email"
            rules={{ required: true, pattern: Regexes.EMAIL_REGEX }}
            control={control}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <StyledInput
                title={t('common.email')}
                type="email"
                value={value}
                onChange={onChange}
                placeholder={t('common.email')}
                id="email"
                valid={!error}
                errorMessage={error?.message || t('error.valid.email')}
                required
                dataTestId={authTestIDs.login.email}
              />
            )}
          />

          <Flex flexDirection="column" width={'100%'} gap="s">
            <Controller
              name="password"
              control={control}
              rules={{ required: true }}
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <StyledInput
                  title={t('common.password')}
                  value={value}
                  type="password"
                  onChange={onChange}
                  placeholder={t('common.password')}
                  id="password"
                  valid={!error}
                  errorMessage={error?.message || t('signup.error.password')}
                  required
                  dataTestId={authTestIDs.login.password}
                />
              )}
            />

            <PasswordButton
              onClick={() => multiLangNavigate('/forgot-password')}
              title={t('login.forgot_password')}
            />
          </Flex>

          <Flex flexDirection="column" gap="m" width={'100%'}>
            <Text fontSize={'s'} color="fgSecondary">
              {t('app.beta.title')}
            </Text>
            <Text fontSize={'s'} color="fgSecondary">
              <Trans
                i18nKey={'login.terms_privacy.agree'}
                components={[
                  <PrivacyTermsLink
                    href={`https://www.atiralink.com/${i18n.language !== AppLangs.EN ? `${i18n.language}/` : ''}privacy-policy`}
                    target="_blank"
                  />,
                  <PrivacyTermsLink
                    href={`https://www.atiralink.com/${i18n.language !== AppLangs.EN ? `${i18n.language}/` : ''}privacy-policy`}
                    target="_blank"
                  />,
                ]}
              />
            </Text>

            <StyledButton
              loading={loading}
              onClick={handleSubmit(onNext)}
              dataTestId={authTestIDs.login.submit}
            >
              {t('common.login')}
            </StyledButton>

            <GoogleButton
              returnPath={LoginWithGoogleReturnPath.LOGIN}
              dataTestId={authTestIDs.google.submit}
            >
              {t('sign_in_with_google')}
            </GoogleButton>

            <StyledHr />

            <Flex gap="s" alignItems="center">
              <Text>{t('login.do_not_have_account')}</Text>

              <SignUpLink keepParams to={'/signup'}>
                {t('common.signup')}
              </SignUpLink>
            </Flex>
          </Flex>
        </InnerWrapper>
      </OuterWrapper>
    </RegisterWrapper>
  );
};
